import { openDB } from 'idb';
import { isServer } from './utils';

// Queue for cache updates
const cacheUpdateQueue = [];
let isCacheUpdating = false;

// Check if IndexedDB is available
const isIndexedDBAvailable = () => !isServer() && window?.indexedDB;
const DB_NAME = 'api-cache';
const STORE_NAME = 'cache';

// size limits
const MAX_CACHE_BYTES = 100 * 1024 * 1024; // 100MB
const EVICT_BYTES = 10 * 1024 * 1024; // 10MB

// Create or connect to the database
const dbPromise = isIndexedDBAvailable()
  ? openDB(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
    })
  : null;

export async function clearApiCache() {
  if (!isIndexedDBAvailable()) return;
  try {
    const db = await dbPromise;
    await db.clear(STORE_NAME);
  } catch (error) {
    console.error('Error clearing API cache:', error);
  }
}

async function enforceCacheLimit() {
  if (!isIndexedDBAvailable()) return;

  const db = await dbPromise;
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);

  const entries: { key: string; size: number; lastAccess: number }[] = [];

  for await (const cursor of store) {
    const rec = JSON.parse(cursor.value);
    entries.push({
      key: cursor.key as string,
      size: rec.size || new Blob([cursor.value]).size,
      lastAccess: rec.lastAccess || 0,
    });
  }

  const totalSize = entries.reduce((sum, e) => sum + e.size, 0);

  if (totalSize > MAX_CACHE_BYTES) {
    entries.sort((a, b) => a.lastAccess - b.lastAccess);
    let freed = 0;
    for (const e of entries) {
      if (freed >= EVICT_BYTES) break;
      await store.delete(e.key);
      freed += e.size;
    }
  }

  await tx.done;
}

export async function getCachedData(key: string) {
  if (!isIndexedDBAvailable()) return null;

  try {
    const db = await dbPromise;
    const raw = await db.get(STORE_NAME, key);
    if (!raw) return null;

    const data = JSON.parse(raw);

    // update lastAccess for LRU
    data.lastAccess = Date.now();
    await db.put(STORE_NAME, JSON.stringify(data), key);

    return data;
  } catch (error) {
    console.error('Error getting cached data:', error);
  }
}

export async function saveDataToCache(key: string, data: any) {
  if (!isIndexedDBAvailable()) return null;

  try {
    const size = new Blob([JSON.stringify(data)]).size;
    const entry = {
      ...data,
      size,
      lastAccess: Date.now(),
    };
    const db = await dbPromise;
    await db.put(STORE_NAME, JSON.stringify(entry), key);
    await enforceCacheLimit();
  } catch (error) {
    console.error('Error saving data to cache:', error);
  }
}

export async function deleteDataFromCache(key: string) {
  if (!isIndexedDBAvailable()) return null;

  try {
    const db = await dbPromise;
    await db.delete(STORE_NAME, key);
  } catch (error) {
    console.error('Error deleting data from cache:', error);
  }
}

// Add data to the cache queue
export async function addToCacheQueue(key: string, data: any) {
  cacheUpdateQueue.push({ key, data });

  // Process the queue in the background
  await processCacheUpdateQueue();
}

async function processCacheUpdateQueue() {
  if (isCacheUpdating || cacheUpdateQueue.length === 0) {
    return null;
  }

  isCacheUpdating = true;

  while (cacheUpdateQueue.length > 0) {
    const { key, data } = cacheUpdateQueue.shift();
    try {
      await saveDataToCache(key, data);
    } catch (error) {
      console.error('Failed to save data to cache:', error);
    }
  }

  isCacheUpdating = false;
}
